import { Col, Modal, Row, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { useEffect, useState } from 'react';
import { BsCheckLg } from 'react-icons/bs';
import { FaRegListAlt } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import IntlMessages from '../../../../../../../domain/utility/IntlMessages';
import Button from '../../../../../../../shared/components/button';
import { AttributesOptionListDto } from '../../../../../../../shared/services/specification-attribute/dto/attributes-option-dto';
import './index.less';
import languageData from '../../../../../../../domain/core/AppLanguageSwitcher/data';
import LanguageCode from '../../../../../../../shared/services/types/language-code';
import OptionModalLanguage from './option-modal-language';
import { schema } from './schema';
import { specificationAttributeService } from '../../../../../../../shared/services/specification-attribute';
import { useParams } from 'react-router-dom';
import MainUtils from 'src/shared/utils/main';
import { useLocaleContext } from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import { getLocaleValue } from 'src/shared/functions/change-locales';

interface IOptionModalProps {
  visible: boolean;
  attributeOptionDetails?: AttributesOptionListDto;
  onCancel: () => void;
  onFinish: () => void;
}

const OptionModal = (props: IOptionModalProps) => {
  const { visible, attributeOptionDetails, onCancel, onFinish } = props;

  const { messages } = useIntl();

  const { id } = useParams();

  const [initialValuesForm, setInitialValuesForm] = useState<any>();

  const { locale }: any = useLocaleContext();

  useEffect(() => {
    if (attributeOptionDetails && visible)
      setInitialValuesForm({
        name: attributeOptionDetails?.Name,
        id: attributeOptionDetails?.Id,
        locales:
          attributeOptionDetails?.Locales?.map((item) => {
            return {
              id: item.Id,
              languageId: item.LanguageId,
              localeKey: item.LocaleKey,
              localeValue: item.LocaleValue,
            };
          }) ?? [],
      });
    else
      setInitialValuesForm({
        name: '',
        locales: [],
      });
  }, [attributeOptionDetails, visible]);

  const handleSubmit = async (values: any, setSubmitting: any) => {
    try {
      if (!attributeOptionDetails) {
        await specificationAttributeService.createOptionBySpecificationAttribute(
          {
            ...values,
            specificationAttributeId: id,
            name: !MainUtils.isEmptyValue(
              getLocaleValue(values?.locales, LanguageCode.English, 'name'),
            )
              ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
              : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
          },
        );
      } else {
        await specificationAttributeService.editOptionBySpecificationAttribute({
          ...values,
          specificationAttributeId: id,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
        });
      }
      await onFinish();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues: any = {
    name: '',
  };

  const langComponent = [
    {
      code: `${LanguageCode.English}`,
      langComponent: <OptionModalLanguage code={LanguageCode.English} />,
    },
    {
      code: `${LanguageCode.Arabic}`,
      langComponent: <OptionModalLanguage code={LanguageCode.Arabic} />,
    },
    {
      code: `${LanguageCode.Español}`,
      langComponent: <OptionModalLanguage code={LanguageCode.Español} />,
    },
  ];

  const itemsTab: TabsProps['items'] = [
    {
      key: 'standard',
      label: `${messages[
        'specificationAttributes.edit.option.standard'
      ].toString()}`,
      children: (
        <OptionModalLanguage
          code={`${!MainUtils.isEmptyValue(locale.code)
            ? locale.code
            : LanguageCode.English
            }`}
        />
      ),
    },
    ...languageData
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((language) => {
        return {
          key: language.code,
          label: (
            <>
              <Row gutter={7}>
                <Col>
                  <i className={`flag flag-24 flag-${language.icon}`} />
                </Col>
                <Col>
                  <h4 className='title-tab'>{language.name}</h4>
                </Col>
              </Row>
            </>
          ),
          children: (
            <>
              {langComponent && langComponent?.length > 0 ? (
                langComponent?.find((item) => item.code === language.code)
                  ?.langComponent ?? <></>
              ) : (
                <></>
              )}
            </>
          ),
        };
      }),
  ];

  return (
    <Formik
      enableReinitialize
      initialValues={attributeOptionDetails ? initialValuesForm : initialValues}
      validationSchema={schema(messages)}
      onSubmit={(values, { setSubmitting }: any) =>
        handleSubmit(values, setSubmitting)
      }
      validateOnChange={false}
      validateOnBlur={false}>
      {({ handleSubmit, isSubmitting }: any) => (
        <Form>
          <Modal
            className='add-option-modal'
            width={'700px'}
            open={visible}
            maskClosable={false}
            title={
              <>
                <div className='text-title'>
                  <span className='icon-title'>
                    <FaRegListAlt />
                  </span>
                  {attributeOptionDetails ? (
                    <IntlMessages id='specificationAttributes.edit.editOptionTitleModal' />
                  ) : (
                    <IntlMessages id='specificationAttributes.edit.addOptionTitleModal' />
                  )}
                </div>
              </>
            }
            onCancel={onCancel}
            footer={false}>
            <Row gutter={[0, 20]}>
              <Col span={24}>
                <Row justify='end'>
                  <Col>
                    <Button
                      icon={<BsCheckLg className='add-button-icon' />}
                      onClickAction={handleSubmit}
                      text={messages['common.save'].toString()}
                      type='primary'
                      size='small'
                      className='add-button'
                      loading={isSubmitting}
                    />
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Tabs defaultActiveKey='standard' items={itemsTab} />
              </Col>
            </Row>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default OptionModal;
